import { createScopedLogger } from '~/utils/logger';
import {
  checkBoltHistoryExists,
  openBoltDatabase,
  openDatabase,
  getAll as getAllChats,
  setMigratedMessages,
} from './db';
import { chatId, type ChatHistoryItem } from './useChatHistory';
import type { StoreFileAction } from '~/types/actions';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/remix';
import { userStore } from '~/lib/stores/user';

const logger = createScopedLogger('MigrateChatHistory');

interface BoltChatHistoryItem {
  id: string;
  messages: any[];
  urlId?: string;
  description?: string;
  timestamp: string;
}

// Get all chats from boltHistory
async function getAllBoltChats(db: IDBDatabase): Promise<BoltChatHistoryItem[]> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result as BoltChatHistoryItem[]);
    request.onerror = () => reject(request.error);
  });
}

// Main migration function
export async function migrateChatHistory(
  parseMessagesForMigration: (messages: any[]) => StoreFileAction[],
): Promise<boolean> {
  const { email } = userStore.get();

  try {
    // Check if migration is already completed with the alpha-migrated-to-v2 flag
    const migrationCompleted = localStorage.getItem('alpha-migrated-to-v2') === 'true';

    if (migrationCompleted) {
      logger.info('Migration already completed, skipping');
      return true;
    }

    // Check if boltHistory exists
    const exists = await checkBoltHistoryExists();

    if (!exists) {
      logger.info('No boltHistory database found, migration not needed');
      localStorage.setItem('alpha-migrated-to-v2', 'true');
      return true;
    }

    // Open both databases
    const boltDb = await openBoltDatabase();
    const dualiteDb = await openDatabase();

    if (!boltDb || !dualiteDb) {
      logger.error('Failed to open databases for migration');
      return false;
    }

    // Get all chats from boltHistory
    const boltChats = await getAllBoltChats(boltDb);
    logger.info(`Found ${boltChats.length} chats to migrate`);

    if (boltChats.length === 0) {
      logger.info('No chats to migrate');

      // Close and delete the empty database
      boltDb.close();

      // await deleteBoltHistory();
      localStorage.setItem('alpha-migrated-to-v2', 'true');
      return true;
    }

    // Migrate each chat
    let successCount = 0;
    let failureCount = 0;

    for (const chat of boltChats) {
      try {
        // Skip invalid chats
        if (!chat.id || !Array.isArray(chat.messages) || chat.messages.length === 0) {
          logger.warn(`Skipping invalid chat with ID ${chat.id || 'unknown'}`);
          failureCount++;
          continue;
        }

        // Extract fileActions from messages
        const fileActions = parseMessagesForMigration(chat.messages);

        logger.info(
          `Processing chat ${chat.id} with ${chat.messages.length} messages, extracted ${fileActions.length} file actions`,
        );

        // Store in dualiteHistory
        await setMigratedMessages(
          dualiteDb,
          chat.id,
          chat.messages,
          fileActions,
          chat.timestamp,
          chat.urlId,
          chat.description,
        );

        logger.info(`Migrated chat ${chat.id} successfully`);
        successCount++;
      } catch (error) {
        Sentry.captureException(error, {
          extra: { chatId: chat.id, messages: chat.messages, message: 'Failure in chat migration' },
        });
        logger.error(`Failed to migrate chat ${chat.id}:`, error);
        failureCount++;
      }
    }

    logger.info(`Migration summary: ${successCount} chats migrated successfully, ${failureCount} failed`);

    // Close bolt database before deletion
    boltDb.close();

    // Delete boltHistory database after migration
    if (successCount > 0) {
      try {
        // await deleteBoltHistory();
        logger.info('boltHistory database deleted successfully');
      } catch (error) {
        Sentry.captureException(error, {
          extra: { message: 'Failure in deleting boltHistory database' },
        });
        logger.error('Failed to delete boltHistory database:', error);

        // Continue anyway, as the migration was successful
      }
    }

    // Set migration flag to prevent repeated attempts
    localStorage.setItem('alpha-migrated-to-v2', 'true');

    logger.info('Migration completed successfully');
    return successCount > 0;
  } catch (error) {
    Sentry.captureException(error, {
      extra: { message: 'Error during chat history migration' },
    });
    logger.error('Migration failed:', error);
    toast.error('Failed to migrate chat history from boltHistory to dualiteHistory');
    return false;
  }
}
